import React from 'react';
import { useTranslation } from 'react-i18next';
import { DOCUMENT_MAX_SIZE_BYTES, PAYSLIP_TYPES, type PayslipType } from '@timesheet/schemas';
import { Button } from '../../ui/button';
import { Checkbox } from '../../ui/checkbox';
import { Label } from '../../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../ui/select';
import { FileDropZone } from '../FileDropZone';
import { monthNames } from '../documents.utils';
import type { PayslipPeriod } from './usePayslipUploadJobs';

interface PayslipPeriodStepProps {
  period: PayslipPeriod;
  onPeriodChange: (period: PayslipPeriod) => void;
  files: File[];
  onFilesChange: (files: File[]) => void;
  extractBalances: boolean;
  onExtractChange: (value: boolean) => void;
  onNext: () => void;
}

/** Step 1: period, kind and the PDF files (one per employee or one multi-employee PDF). */
export const PayslipPeriodStep: React.FC<PayslipPeriodStepProps> = ({
  period,
  onPeriodChange,
  files,
  onFilesChange,
  extractBalances,
  onExtractChange,
  onNext,
}) => {
  const { t, i18n } = useTranslation();
  const months = monthNames(i18n.language);
  const currentYear = new Date().getFullYear();
  const years = Array.from({ length: 6 }, (_, i) => currentYear + 1 - i);
  const isYearly = period.type === 'CU';
  const canExtract = period.type === 'MENSILE';

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
        <div className="space-y-1.5">
          <Label>{t('documents.payslipUpload.period.type', 'Tipo')}</Label>
          <Select
            value={period.type}
            onValueChange={(value) => {
              const type = value as PayslipType;
              onPeriodChange({ ...period, type, month: type === 'CU' ? undefined : (period.month ?? 1) });
              if (type !== 'MENSILE') onExtractChange(false);
            }}
          >
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {PAYSLIP_TYPES.map((type) => (
                <SelectItem key={type} value={type}>
                  {t(`documents.payslipTypes.${type}`, type)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-1.5">
          <Label>{t('documents.payslipUpload.period.month', 'Mese')}</Label>
          <Select
            value={period.month ? String(period.month) : ''}
            onValueChange={(value) => onPeriodChange({ ...period, month: Number(value) })}
            disabled={isYearly}
          >
            <SelectTrigger>
              <SelectValue placeholder={isYearly ? '—' : t('documents.payslipUpload.period.month', 'Mese')} />
            </SelectTrigger>
            <SelectContent>
              {months.map((name, index) => (
                <SelectItem key={name} value={String(index + 1)}>
                  {name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-1.5">
          <Label>{t('documents.payslipUpload.period.year', 'Anno')}</Label>
          <Select value={String(period.year)} onValueChange={(value) => onPeriodChange({ ...period, year: Number(value) })}>
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {years.map((year) => (
                <SelectItem key={year} value={String(year)}>
                  {year}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      <FileDropZone
        accept=".pdf,application/pdf"
        multiple
        maxSizeBytes={DOCUMENT_MAX_SIZE_BYTES}
        files={files}
        onFilesChange={onFilesChange}
        hint={t('documents.payslipUpload.period.dropHint', 'PDF singoli o un PDF con più dipendenti (verrà separato per codice fiscale)')}
      />

      <label className="flex cursor-pointer items-start gap-3 rounded-md border p-3 text-sm">
        <Checkbox checked={extractBalances && canExtract} disabled={!canExtract} onCheckedChange={(value) => onExtractChange(value === true)} className="mt-0.5" />
        <span>
          <span className="font-medium">{t('documents.payslipUpload.period.extract', 'Estrai i saldi ferie, permessi e ROL')}</span>
          <span className="block text-xs text-muted-foreground">
            {canExtract
              ? t('documents.payslipUpload.period.extractHint', 'I saldi alimentano il riepilogo nelle richieste ferie del dipendente.')
              : t('documents.payslipUpload.period.extractDisabled', 'Disponibile solo per la busta paga mensile.')}
          </span>
        </span>
      </label>

      <div className="flex justify-end">
        <Button className="w-full bg-orange-500 hover:bg-orange-600 sm:w-auto" disabled={files.length === 0 || (!isYearly && !period.month)} onClick={onNext}>
          {t('documents.payslipUpload.period.next', 'Avanti')}
        </Button>
      </div>
    </div>
  );
};
